import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Printer, Edit2, Trash2, DollarSign, RefreshCw, Search, Filter, ShoppingCart, AlertTriangle } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  pending_bom: 'bg-orange-100 text-orange-800',
  pending_variant_selection: 'bg-amber-100 text-amber-800',
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  in_production: 'bg-purple-100 text-purple-800',
  partially_dispatched: 'bg-teal-100 text-teal-800',
  completed: 'bg-green-100 text-green-800',
  dispatched: 'bg-green-200 text-green-900',
  cancelled: 'bg-red-100 text-red-800',
  deleted: 'bg-red-200 text-red-900 line-through'
};

const statusLabels = {
  draft: 'Draft', pending_bom: 'Pending BOM', pending_variant_selection: 'Pending Variants',
  pending: 'Pending', confirmed: 'Confirmed', in_production: 'In Production',
  partially_dispatched: 'Partial Dispatch', completed: 'Completed', dispatched: 'Dispatched', 
  cancelled: 'Cancelled', deleted: 'Deleted'
};

export default function SORegisterView({
  orders,
  clients,
  workOrders,
  woDeliveryStatus,
  canSeePrices,
  canCreateSO,
  canEditSO,
  canDeleteSO,
  isLoading,
  searchTerm,
  statusFilter,
  currentPage,
  totalPages,
  totalOrders,
  onSelectSO,
  onSearch,
  onStatusFilter,
  onPageChange,
  onOpenCreate,
  onOpenClient,
  onRefresh,
  onPrint,
  onOpenPayment,
  onEdit,
  onDelete,
  onFixVariantStatus,
  calculateSOStatus,
  enrichOrderWithMasterData,
}) {
  const getEnrichedOrders = () => {
    // Note: Filtering is done server-side now, so we just enrich the orders
    return orders.map(order => {
      const enriched = enrichOrderWithMasterData(order);
      const statusInfo = calculateSOStatus(order);
      return { ...enriched, calculated_status: statusInfo.status, status_color: statusInfo.color };
    });
  };

  const enrichedOrders = getEnrichedOrders();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search SO number, client..."
              value={searchTerm}
              onChange={onSearch}
              className="pl-9 h-9"
              data-testid="so-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={onStatusFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="so-status-filter">
              <Filter className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending_bom">Pending BOM</SelectItem>
              <SelectItem value="pending_variant_selection">Pending Variants</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="in_production">In Production</SelectItem>
              <SelectItem value="dispatched">Dispatched</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-so-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          {canCreateSO && (
            <Button variant="outline" size="sm" onClick={onOpenClient}>
              <Plus className="w-4 h-4 mr-1.5" /> New Client
            </Button>
          )}
          {canCreateSO && (
            <Button size="sm" onClick={onOpenCreate} data-testid="create-so-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create Sales Order
            </Button>
          )}
        </div>
      </div>

      {/* Top Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1">
          <Button variant="outline" size="sm" onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading}>First</Button>
          <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>Prev</Button>
          <span className="px-3 text-sm text-muted-foreground">Page {currentPage}/{totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>Next</Button>
          <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>Last</Button>
        </div>
      )}

      {/* Table */}
      <Card className="border overflow-hidden">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="w-[140px]">SO Number</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-center w-[70px]">Items</TableHead>
                  <TableHead className="w-[100px]">Delivery</TableHead>
                  {canSeePrices && <TableHead className="text-right w-[120px]">Amount</TableHead>}
                  <TableHead className="w-[150px]">Dispatch</TableHead>
                  <TableHead className="w-[160px]">Status</TableHead>
                  <TableHead className="text-right w-[130px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrichedOrders.map((order) => {
                  const client = clients.find(c => c.id === order.customer_id);
                  const lineItems = order.line_items || [];
                  const totalDispatched = lineItems.reduce((s, li) => s + (li.dispatched_quantity || 0), 0);
                  const totalQty = lineItems.reduce((s, li) => s + (li.quantity || 0), 0);
                  const dispatchPct = totalQty > 0 ? Math.round((totalDispatched / totalQty) * 100) : 0;
                  const deliveryData = woDeliveryStatus[order.id];
                  const isDeleted = order.status === 'deleted';

                  return (
                    <TableRow
                      key={order.id}
                      className={`transition-colors ${isDeleted ? 'bg-red-50/50 opacity-60' : 'cursor-pointer hover:bg-accent/50'}`}
                      onClick={() => !isDeleted && onSelectSO(order.id)}
                      data-testid={`so-row-${order.so_number}`}
                    >
                      <TableCell>
                        <span className={`font-semibold text-sm ${isDeleted ? 'line-through text-muted-foreground' : ''}`}>{order.so_number}</span>
                      </TableCell>
                      <TableCell>
                        <p className={`text-sm font-medium truncate ${isDeleted ? 'text-muted-foreground' : ''}`}>{client?.name || order.customer_name || '-'}</p>
                        {client?.client_code && <p className="text-xs text-muted-foreground">{client.client_code}</p>}
                      </TableCell>
                      <TableCell className="text-center">
                        {isDeleted ? (
                          <span className="text-xs text-muted-foreground">-</span>
                        ) : (
                          <Badge variant="outline" className="text-xs">{lineItems.length}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {isDeleted ? (
                          <div className="text-xs text-muted-foreground">
                            <p>Deleted</p>
                            {order.deleted_at && <p className="text-[10px]">{formatDate(order.deleted_at)}</p>}
                          </div>
                        ) : (
                          <span className="text-sm">{formatDate(order.delivery_date)}</span>
                        )}
                      </TableCell>
                      {canSeePrices && (
                        <TableCell className="text-right">
                          {isDeleted ? (
                            <span className="text-xs text-muted-foreground">-</span>
                          ) : (
                            <span className="font-semibold text-sm">{formatCurrency(order.total_amount || 0)}</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {isDeleted ? (
                          <span className="text-xs text-muted-foreground">-</span>
                        ) : totalQty > 0 ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Progress value={dispatchPct} className="h-1.5 flex-1" />
                              <span className="text-xs text-muted-foreground w-8 text-right">{dispatchPct}%</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{totalDispatched}/{totalQty} dispatched</p>
                          </div>
                        ) : <span className="text-xs text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge className={`text-xs ${statusColors[order.status] || statusColors.pending}`}>
                            {statusLabels[order.status] || order.status}
                          </Badge>
                          {!isDeleted && (order.status === 'pending_bom' || order.status === 'pending_variant_selection') && (
                            <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                          )}
                          {!isDeleted && order.status === 'pending_variant_selection' && (
                            <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-blue-600"
                              onClick={(e) => { e.stopPropagation(); onFixVariantStatus(order.id); }}>
                              Fix
                            </Button>
                          )}
                          {!isDeleted && deliveryData && deliveryData.total_wos > 0 && (() => {
                            const cfg = {
                              'ON_TRACK': { l: 'On Track', c: 'bg-green-100 text-green-700 border-green-300' },
                              'AT_RISK': { l: 'At Risk', c: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
                              'DELAYED': { l: 'Delayed', c: 'bg-red-100 text-red-700 border-red-300' }
                            }[deliveryData.overall_status];
                            return cfg ? <Badge variant="outline" className={`text-[10px] px-1 ${cfg.c}`}>{cfg.l}</Badge> : null;
                          })()}
                          {isDeleted && order.deleted_by_name && (
                            <span className="text-[10px] text-muted-foreground ml-1">by {order.deleted_by_name}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {isDeleted ? (
                          <span className="text-xs text-muted-foreground italic">No actions</span>
                        ) : (
                          <div className="flex items-center justify-end gap-0.5">
                            {canSeePrices && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => onOpenPayment(order)} title="Payment" data-testid={`payment-${order.so_number}`}>
                                <DollarSign className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onPrint(order)} title="Print">
                              <Printer className="w-3.5 h-3.5" />
                            </Button>
                            {canEditSO && order.status !== 'completed' && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(order)} title="Edit">
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {canDeleteSO && order.status !== 'completed' && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => onDelete(order)} title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {!isLoading && orders.length === 0 && (
            <div className="text-center py-16">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
              <p className="text-muted-foreground">{searchTerm ? 'No orders match your search' : 'No sales orders yet'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination + count */}
      <div className="flex flex-col items-center gap-2 pb-16">
        <p className="text-xs text-muted-foreground">
          {isLoading ? 'Loading...' : `Showing ${orders.length} of ${totalOrders} orders`}
        </p>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading}>First</Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>Prev</Button>
            <span className="px-3 text-sm text-muted-foreground">Page {currentPage}/{totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>Next</Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>Last</Button>
          </div>
        )}
      </div>
    </div>
  );
}
